import HttpClientError from '@errors/HttpClientError';
import { type DrugInteraction } from '@models/DrugInteraction';
import HttpClient from '@services/utils/HttpClient';

import { type IGetAllDrugsInputData } from './dtos/getAllDrugs/InputData';
import { type IGetAllDrugsOutputData } from './dtos/getAllDrugs/OutputData';
import { type IVerifyInteractionsBetweenDrugsInputData } from './dtos/verifyInteractionsBetweenDrugs/InputData';
import { type IVerifyInteractionsBetweenDrugsOutputData } from './dtos/verifyInteractionsBetweenDrugs/OutputData';
import { type IDrugRepository } from './IDrugRepository';

interface IGetAllParams {
  id?: number;
}
interface IGetAllResponse {
  lista: {
    e_favorito: boolean;
    id: number;
    nome: string;
  }[];
}

interface IVerifyInteractionsBody {
  ids: number[];
}
interface IVerifyInteractionsResponse {
  lista: {
    bulas?: {
      link: string;
      medicamento: string;
    }[];
    conteudo: string;
    medicamento_interacao: string;
    medicamento_origem: string;
    nivel: string;
  }[];
}

class DrugService implements IDrugRepository {
  public async getAll(
    inputData: IGetAllDrugsInputData,
  ): Promise<IGetAllDrugsOutputData> {
    try {
      const params: IGetAllParams = {};
      if (inputData.typeDrugId) {
        params.id = inputData.typeDrugId;
      }
      const response = await HttpClient.get<IGetAllResponse, IGetAllParams>({
        path: 'medicamentos.php',
        params,
      });
      const output: IGetAllDrugsOutputData = {
        list: response.data.lista.map((item) => ({
          id: item.id,
          isFavorite: item.e_favorito,
          name: item.nome,
        })),
      };
      return output;
    } catch (err) {
      if (err instanceof HttpClientError) {
        throw new HttpClientError({
          body: err.body,
          message: err.message,
          response: err.response,
        });
      }
      if (err instanceof Error) {
        throw new Error(err.message);
      }
      throw new Error(String(JSON.stringify(err)));
    }
  }

  public async verifyInteractions(
    inputData: IVerifyInteractionsBetweenDrugsInputData,
  ): Promise<IVerifyInteractionsBetweenDrugsOutputData> {
    try {
      const response = await HttpClient.post<
        IVerifyInteractionsResponse,
        IVerifyInteractionsBody
      >({
        path: 'interacoes.php',
        body: {
          ids: inputData.ids,
        },
      });
      const output: IVerifyInteractionsBetweenDrugsOutputData = {
        list: response.data.lista.map((item) => {
          const drugInteraction: DrugInteraction = {
            content: item.conteudo,
            interaction: item.medicamento_interacao,
            level: item.nivel,
            origin: item.medicamento_origem,
            labels:
              item.bulas?.map((bula, index) => ({
                drug: bula.medicamento,
                link: bula.link,
                uniqueId: index,
              })) || [],
          };
          return drugInteraction;
        }),
      };
      return output;
    } catch (err) {
      if (err instanceof HttpClientError) {
        throw new HttpClientError({
          body: err.body,
          message: err.message,
          response: err.response,
        });
      }
      if (err instanceof Error) {
        throw new Error(err.message);
      }
      throw new Error(String(JSON.stringify(err)));
    }
  }
}

export default new DrugService();
